import { useEffect, useRef, useState } from 'react'
import { easeOutCubic, prefersReducedMotion } from '../../shared/motion.ts'

/**
 * Tween a numeric display value toward `target` with ease-out cubic.
 * Used for balances, multipliers, odds — display only, never game math.
 * Jumps instantly under reduced motion.
 */
export function useCountUp(target: number, duration = 500): number {
  const [value, setValue] = useState(target)
  const fromRef = useRef(target)
  const rafRef = useRef(0)

  useEffect(() => {
    if (prefersReducedMotion() || duration <= 0) {
      fromRef.current = target
      setValue(target)
      return
    }
    const from = fromRef.current
    if (from === target) return
    const start = performance.now()
    const tick = (now: number) => {
      const p = Math.max(0, Math.min(1, (now - start) / duration))
      const e = easeOutCubic(p)
      const v = from + (target - from) * e
      fromRef.current = v
      setValue(v)
      if (p < 1) rafRef.current = requestAnimationFrame(tick)
    }
    rafRef.current = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(rafRef.current)
  }, [target, duration])

  return value
}
